import { graphqlQuery } from '@core/http/graphql-client';

/**
 * Response from appGetProductTypeDetail GraphQL query.
 */
interface ProductTypeDetail {
  id: string;
  code: string;
  name: string;
}

interface AppGetProductTypeDetailResponse {
  appGetProductTypeDetail: ProductTypeDetail | null;
}

/**
 * API for product-related GraphQL operations.
 */
export class ProductAPI {
  /**
   * Fetches product type details by ID via GraphQL.
   *
   * GraphQL query: appGetProductTypeDetail(productTypeId: String!)
   * Returns: { id, code, name }
   */
  async getProductTypeDetail(productTypeId: string): Promise<ProductTypeDetail | null> {
    const query = `
      query appGetProductTypeDetail($productTypeId: String!) {
        appGetProductTypeDetail(productTypeId: $productTypeId) {
          id
          code
          name
        }
      }
    `;

    const response = await graphqlQuery<AppGetProductTypeDetailResponse>(query, {
      productTypeId,
    });

    return response.appGetProductTypeDetail;
  }

  /**
   * Resolves the product code from a product type ID.
   *
   * Convenience method that calls getProductTypeDetail and returns only the code.
   *
   * @param productTypeId - The UUID of the product type
   * @returns The product code string, or null if not found
   */
  async resolveProductCode(productTypeId: string): Promise<string | null> {
    const detail = await this.getProductTypeDetail(productTypeId);
    return detail?.code ?? null;
  }
}
